import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useTimer } from "@/context/TimerContext";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { DeliverableBadge, DeliverableStatusBadge } from "@/components/Badges";
import { formatDuration, relativeDue } from "@/lib/constants";
import { ClientAvatar } from "@/components/ClientAvatar";
import { MapPin, Loader2, Play, Square, Check, History, AlertTriangle, CalendarClock, ListChecks } from "lucide-react";
import { toast } from "sonner";

function TaskTimer({ task, onChanged }) {
  const { active, start, stop } = useTimer();
  const [busy, setBusy] = useState(false);
  const isActive = active &&
    String(active.deliverable_id) === String(task.deliverable_id) &&
    String(active.order_id) === String(task.order_id);
  const live = isActive ? (active.time_seconds || 0) : (task.time_seconds || 0);

  const click = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      if (isActive) { await stop(); } else { await start(task.order_id, task.deliverable_id); }
      onChanged?.();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <button onClick={click} disabled={busy} data-testid={`task-timer-${task.deliverable_id}`}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
        isActive ? "border-rose-500/40 bg-rose-500/15 text-rose-500" : "border-border bg-secondary/50 text-muted-foreground hover:text-foreground"}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : isActive ? <Square className="h-3.5 w-3.5 fill-current" /> : <Play className="h-3.5 w-3.5" />}
      {formatDuration(live)}
    </button>
  );
}

function TaskRow({ task, onChanged }) {
  const navigate = useNavigate();
  const [delivering, setDelivering] = useState(false);

  const markDone = async (e) => {
    e.stopPropagation();
    setDelivering(true);
    try {
      await api.patch(`/orders/${task.order_id}/deliverables/${task.deliverable_id}`, { status: "DELIVERED" });
      toast.success("Marked delivered");
      onChanged?.();
    } catch (err) { toast.error(apiError(err)); }
    finally { setDelivering(false); }
  };

  const overdue = task.due_date && task.due_date < new Date().toISOString().slice(0, 10);

  return (
    <div onClick={() => navigate(`/orders/${task.order_id}`)} data-testid={`task-row-${task.deliverable_id}`}
      className="group flex cursor-pointer flex-col gap-2 rounded-xl border border-border bg-card px-4 py-3 transition-colors hover:border-primary/40 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <DeliverableBadge type={task.type} />
          <DeliverableStatusBadge status={task.status} />
          {task.is_reshoot && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-500">
              <History className="h-3 w-3" /> Reshoot
            </span>
          )}
          <span className={`text-xs font-semibold ${overdue ? "text-rose-500" : "text-muted-foreground"}`} title={task.due_date || ""} data-testid={`task-due-${task.deliverable_id}`}>
            {relativeDue(task.due_date)}
          </span>
        </div>
        <div className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
          <MapPin className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate">{task.address || `Order #${task.order_number}`}</span>
        </div>
        {task.client_name && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <ClientAvatar name={task.client_name} className="h-4 w-4 text-[7px]" />{task.client_name}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2 sm:pl-3">
        <TaskTimer task={task} onChanged={onChanged} />
        <button onClick={markDone} disabled={delivering} data-testid={`task-deliver-${task.deliverable_id}`}
          className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-500 transition-colors hover:bg-emerald-500/25">
          {delivering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          Done
        </button>
      </div>
    </div>
  );
}

function Section({ title, icon: Icon, tone, tasks, onChanged, emptyText, testid }) {
  return (
    <div data-testid={testid}>
      <div className="mb-2 flex items-center gap-2">
        <Icon className={`h-4 w-4 ${tone}`} />
        <h2 className="font-heading text-sm font-bold uppercase tracking-wide">{title}</h2>
        <span className="rounded-full bg-secondary px-2 py-0.5 text-xs text-muted-foreground">{tasks.length}</span>
      </div>
      {tasks.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        <div className="space-y-2">
          {tasks.map((t) => <TaskRow key={t.deliverable_id} task={t} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  );
}

export default function MyTasks() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/my-tasks");
      setData(data);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const firstName = (user?.name || "there").split(" ")[0];
  const activeCount = data ? data.counts.overdue + data.counts.today : 0;

  return (
    <div>
      <PageHeader title="My Tasks"
        subtitle={data?.editor ? `Assigned editing work${activeCount ? ` — ${activeCount} to do now` : ""}` : "Your assigned work"}
        testid="my-tasks-header" />
      <div className="mx-auto max-w-3xl space-y-8 p-6 md:p-8">
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : !data?.editor ? (
          <div className="rounded-xl border border-dashed border-border py-16 text-center" data-testid="my-tasks-empty-nolink">
            <ListChecks className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-3 text-sm text-muted-foreground">You don't have any assigned tasks yet. Work assigned to you by the studio will show up here.</p>
          </div>
        ) : (
          <>
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight">Hi {firstName} 👋</h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {activeCount === 0 ? "You're all caught up — nothing due right now." : `You have ${activeCount} ${activeCount === 1 ? "task" : "tasks"} to work on today.`}
              </p>
            </div>
            <Section title="Overdue" icon={AlertTriangle} tone="text-rose-500" tasks={data.overdue}
              onChanged={load} emptyText="Nothing overdue. 🎉" testid="tasks-overdue" />
            <Section title="Due today" icon={CalendarClock} tone="text-amber-500" tasks={data.today}
              onChanged={load} emptyText="Nothing due today." testid="tasks-today" />
            <Section title="Upcoming" icon={ListChecks} tone="text-primary" tasks={data.upcoming}
              onChanged={load} emptyText="No upcoming tasks assigned." testid="tasks-upcoming" />
          </>
        )}
      </div>
    </div>
  );
}
